/**
 * Global analytics utilities.
 */

import TagManager from 'react-gtm-module';

import uuidv4 from './uuid';

/**
 * Google Tag Manager methods.
 *
 * @type {{trackPageView: gtm.trackPageView, trackEvent: gtm.trackEvent}}
 */
const gtm = {
  /**
   * Tracks a page view.
   *
   * @param {Object} options The page view options.
   * @param {string} options.url The page url.
   * @param {string} [options.title] The page title.
   * @param {string} options.userId The user id.
   */
  trackPageView: ({ url, title, userId, ...data }) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'newPage',
        newPageData: {
          url,
          title: title || 'Virtual Pageview',
          userId,
          ...data,
        },
      },
    });
  },

  trackCustomEvent: ({ event, ...data }) => {
    TagManager.dataLayer({
      dataLayer: {
        event,
        ...data,
      },
    });
  },

  /**
   * Tracks an event.
   * See Google Analytics anatomy of events: Https://support.google.com/analytics/answer/1033068?hl=en.
   *
   * @param {Object} options The event options.
   * @param {string} options.type The event type (ie general category / grouping).
   * @param {string} options.action The event action (ie Click, Load, etc).
   * @param {string} options.label The eventt label (ie the title of a Video, the originating View, etc).
   */
  trackEvent: ({ type, action, label, ...data }) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'newEvent',
        newEventData: {
          type,
          action,
          label,
          ...data,
        },
      },
    });
  },

  trackEcommerce: ({ event = 'purchase', data }) => {
    TagManager.dataLayer({
      dataLayer: {
        ecommerce: null, // Clear the previous ecommerce object
      },
    });

    TagManager.dataLayer({
      dataLayer: {
        event,
        ecommerce: {
          purchase: {
            actionField: {
              id: data.id ?? uuidv4(),
              productId: data.productId ?? null,
              revenue: data.revenue,
              tax: data.tax,
              shipping: data.shipping,
            },
          },
        },
      },
    });
  },
};

export { gtm };
